import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Rocket, CheckCircle, Clock, Users, Zap, Award, Phone, Mail, ArrowRight, Star } from 'lucide-react';

const LaunchPad5Page: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    business: '',
    service: '',
    budget: '',
    timeline: '',
    message: ''
  });

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError('');

    try {
      const response = await fetch('/api/launchpad5', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(form),
      });

      const data = await response.json();

      if (response.ok) {
        setFormSubmitted(true);
        setForm({
          name: '',
          email: '',
          phone: '',
          business: '',
          service: '',
          budget: '',
          timeline: '',
          message: ''
        });
      } else {
        throw new Error(data.error || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error submitting application:', error);
      setFormError('There was an error submitting your application. Please try again or contact us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const serviceBundles = [
    {
      title: 'Business Setup Pack',
      price: '₹1.25 Lakhs',
      originalPrice: '₹62,500',
      description: 'For startups, small businesses, and growing founders.',
      features: [
        'Custom Website',
        'WhatsApp CRM Setup',
        'Insta & Google Profile Optimization',
        'Logo + 5 Design Banners',
        'Delivery in 7 Days'
      ],
      offer: 'Now Only: ₹62,500 upfront',
      popular: false
    },
    {
      title: 'ERP + App System',
      price: '₹2.5 Lakhs',
      originalPrice: '₹1,00,000',
      description: 'For SMEs, product companies, agencies, and service businesses.',
      features: [
        'ERP with Dashboard + App',
        'Login system for Admin, Team, Clients',
        'Invoicing, Projects, Reminders',
        'WhatsApp & Email Alerts',
        'Lifetime Support'
      ],
      offer: '₹1,00,000 upfront, rest after 10 days',
      popular: true
    },
    {
      title: 'Growth Automation Pack',
      price: '₹75,000',
      originalPrice: '₹75,000',
      description: 'For consultants, creators, and marketing teams.',
      features: [
        'WhatsApp Menu Bot',
        'Google Ads Setup',
        'Email Funnel Automation',
        'Google My Business SEO',
        'AI Assistant Setup'
      ],
      offer: 'Flat Price: ₹75,000 (One-time)',
      popular: false
    }
  ];

  const targetAudience = [
    'Retail Business Owners',
    'Startup Founders',
    'Coaches & Course Sellers',
    'Traders, Exporters & Manufacturers',
    'Service Providers (Doctors, Architects, Freelancers)'
  ];

  const whyChooseUs = [
    '7-Day Delivery, Guaranteed',
    'Built by AI + Human Intelligence',
    'Fixed Pricing, No Surprises',
    'Post-launch Support Included',
    'Exclusive Founder-Led Projects (Only 5 Clients/Week)'
  ];

  const processSteps = [
    'Fill the form below with your business needs',
    'Our core team will reach out to you within 12 hours',
    'We\'ll send you a proposal and schedule onboarding',
    'You pay advance, and delivery begins immediately'
  ];

  return (
    <>
      <Helmet>
        <title>VB LaunchPad 5 – Limited July Onboarding | VB Entreprise</title>
        <meta name="description" content="Limited July onboarding for visionary businesses. Custom ERP, websites & automation delivered in 7 days. Only 5 slots available this week." />
      </Helmet>
      
      <div className="pt-8 bg-white min-h-screen text-gray-900">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-jordy-blue-50 to-jordy-blue-100 text-center">
          <div className="max-w-4xl mx-auto px-4">
            <div className="flex items-center justify-center mb-4">
              <Rocket className="w-8 h-8 text-jordy-blue-600 mr-2" />
              <span className="text-jordy-blue-600 font-semibold">VB LaunchPad 5</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-jordy-blue-800">
              Onboarding 5 Businesses This Week for Custom ERP, Websites & Automation
            </h1>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto mb-8 text-gray-700">
              Everything your business needs – Delivered in 7 Days. Limited July Slots Now Open.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button 
                onClick={() => document.getElementById('apply-form')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-jordy-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-jordy-blue-700 transition-colors shadow-lg flex items-center"
              >
                📥 Apply Now
                <ArrowRight className="w-5 h-5 ml-2" />
              </button>
              <div className="flex items-center text-jordy-blue-700">
                <Clock className="w-5 h-5 mr-2" />
                <span className="font-semibold">Only 5 Slots Available</span>
              </div>
            </div>
          </div>
        </section>

        {/* Service Bundles */}
        <section className="py-16 max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center">Our High-Impact Service Bundles</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {serviceBundles.map((bundle, index) => (
              <div key={index} className={`relative bg-white rounded-xl shadow-lg p-8 border-2 ${bundle.popular ? 'border-jordy-blue-500' : 'border-gray-200'}`}>
                {bundle.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-jordy-blue-600 text-white px-4 py-2 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                <h3 className="text-2xl font-bold mb-2 text-jordy-blue-800">{bundle.title}</h3>
                <div className="mb-4">
                  <span className="text-3xl font-bold text-jordy-blue-600">{bundle.price}</span>
                  <span className="text-lg text-gray-500 ml-2 line-through">{bundle.originalPrice}</span>
                </div>
                <p className="text-gray-600 mb-6">{bundle.description}</p>
                <ul className="space-y-3 mb-6">
                  {bundle.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-jordy-blue-50 p-4 rounded-lg mb-6">
                  <p className="text-jordy-blue-800 font-semibold text-center">🎁 {bundle.offer}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Who This Is For */}
        <section className="py-16 bg-jordy-blue-50">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-3xl font-bold mb-8 text-center">Who This Is For</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {targetAudience.map((audience, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-md">
                  <CheckCircle className="w-6 h-6 text-green-500 mb-3" />
                  <h3 className="font-semibold text-gray-800">{audience}</h3>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose VB Entreprise */}
        <section className="py-16 max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8 text-center">Why Choose VB Entreprise?</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {whyChooseUs.map((reason, index) => (
              <div key={index} className="flex items-center bg-jordy-blue-50 rounded-lg p-4">
                <CheckCircle className="w-6 h-6 text-green-500 mr-3 flex-shrink-0" />
                <span className="text-gray-800 font-medium">{reason}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Process Steps */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-3xl font-bold mb-8 text-center">What Happens After You Apply?</h2>
            <div className="space-y-6">
              {processSteps.map((step, index) => (
                <div key={index} className="flex items-start">
                  <div className="bg-jordy-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold mr-4 flex-shrink-0">
                    {index + 1}
                  </div>
                  <p className="text-gray-700 text-lg">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Limited Slots Warning */}
        <section className="py-12 bg-red-50 border-t border-red-200">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold mb-4 text-red-800">🚨 Limited July Slots</h2>
            <p className="text-lg text-red-700 mb-4">
              Only 5 projects will be taken this week. We operate with a founder-led approach, 
              so every project is handled with care, not scale.
            </p>
            <p className="text-red-600 font-semibold">Apply now to secure your onboarding.</p>
          </div>
        </section>

        {/* Application Form */}
        <section id="apply-form" className="py-16 max-w-2xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8 text-center">Apply for July Onboarding</h2>
          
          {!formSubmitted ? (
            <form onSubmit={handleFormSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleFormChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-jordy-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleFormChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-jordy-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleFormChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-jordy-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Business Name *</label>
                  <input
                    type="text"
                    name="business"
                    value={form.business}
                    onChange={handleFormChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-jordy-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Service Package *</label>
                <select
                  name="service"
                  value={form.service}
                  onChange={handleFormChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-jordy-blue-500 focus:border-transparent"
                >
                  <option value="">Select a package</option>
                  <option value="Business Setup Pack">Business Setup Pack - ₹62,500</option>
                  <option value="ERP + App System">ERP + App System - ₹1,00,000</option>
                  <option value="Growth Automation Pack">Growth Automation Pack - ₹75,000</option>
                </select>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Budget Range</label>
                  <select
                    name="budget"
                    value={form.budget}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-jordy-blue-500 focus:border-transparent"
                  >
                    <option value="">Select budget range</option>
                    <option value="₹50,000 - ₹75,000">₹50,000 - ₹75,000</option>
                    <option value="₹75,000 - ₹1,00,000">₹75,000 - ₹1,00,000</option>
                    <option value="₹1,00,000 - ₹2,50,000">₹1,00,000 - ₹2,50,000</option>
                    <option value="₹2,50,000+">₹2,50,000+</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Timeline</label>
                  <select
                    name="timeline"
                    value={form.timeline}
                    onChange={handleFormChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-jordy-blue-500 focus:border-transparent"
                  >
                    <option value="">Select timeline</option>
                    <option value="Immediate (This week)">Immediate (This week)</option>
                    <option value="Within 2 weeks">Within 2 weeks</option>
                    <option value="Within 1 month">Within 1 month</option>
                    <option value="Flexible">Flexible</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Additional Details</label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleFormChange}
                  rows={4}
                  placeholder="Tell us about your business needs, current challenges, and what you hope to achieve..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-jordy-blue-500 focus:border-transparent"
                />
              </div>

              {formError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {formError}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-jordy-blue-600 text-white py-4 rounded-lg font-semibold hover:bg-jordy-blue-700 transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Submitting...
                  </>
                ) : (
                  '📥 Apply for July Onboarding'
                )}
              </button>
            </form>
          ) : (
            <div className="text-center py-12">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Application Submitted!</h3>
              <p className="text-gray-600 mb-4">
                Thank you for your interest in VB LaunchPad 5. Our core team will reach out to you within 12 hours.
              </p>
              <p className="text-sm text-gray-500">
                If you have urgent questions, contact us at{' '}
                <a href="mailto:connect@vbentreprise.com" className="text-jordy-blue-600 hover:underline">
                  connect@vbentreprise.com
                </a>
              </p>
            </div>
          )}
        </section>

        {/* Referral Bonus */}
        <section className="py-12 bg-jordy-blue-50 border-t border-jordy-blue-200">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold mb-4 text-jordy-blue-800">🧧 Referral Bonus</h2>
            <p className="text-lg text-jordy-blue-700 mb-4">
              Know someone who needs this? Refer & Earn ₹10,000 when they onboard this week.
            </p>
          </div>
        </section>

        {/* Contact Information */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-2xl font-bold mb-6">Need Help Deciding?</h2>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <a
                href="https://wa.me/your-business-number"
                className="flex items-center bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors"
              >
                <Phone className="w-5 h-5 mr-2" />
                WhatsApp us
              </a>
              <a
                href="mailto:connect@vbentreprise.com"
                className="flex items-center bg-jordy-blue-600 text-white px-6 py-3 rounded-lg hover:bg-jordy-blue-700 transition-colors"
              >
                <Mail className="w-5 h-5 mr-2" />
                connect@vbentreprise.com
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LaunchPad5Page; 